import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ConditionalWrapper from 'components/application/ConditionalWrapper';
import OptionChoice from 'components/application/OptionChoice';
import Options from 'components/application/Options';
import Switch from 'components/application/Switch';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useTrackedPersonnelContext } from 'components/contexts/TrackedPersonnelContext';

import { sendAnalytics } from 'components/helpers/analytics';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function TrainingOptions({
  booking,
  course,
  courseHasELearningCourse,
  creditInfoUnavailable,
  currentAutoEnrolmentExclusionUserSelection,
  currentPersonnel,
  eLearningCourse,
  isAdditional,
  isAssignedToAnotherRole,
  isCurrentlyRequired,
  isHistoricalTrainingVisible,
  isMultipleTraining,
  isWithinRoleTable,
  onAddBooking,
  onAddELearningBooking,
  onBookingReminderClick,
  onCurrentAutoEnrolmentExclusionUserSelectionChange,
  onDeleteCourseClick,
  onDeleteTrainingClick,
  onEditTrainingClick,
  onNewTrainingClick,
  onRemoveBooking,
  onRequirementChange,
  onTrainingOptionsClose,
  personnelId,
  prioritisedAutoEnrolmentBlockers,
  registration,
  showHistoricalTrainingOnly,
  toggleHistoricalTraining,
  training,
  userCourse,
}) {
  const currentActor = useCurrentActor();
  const trackedPersonnelContext = useTrackedPersonnelContext();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'training.TrainingOptions',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const optionsHandler = (setOptionsOpen, callback) => {
    callback();
    setOptionsOpen(false);
  };

  const prioritisedDeletionStatus = (() => {
    switch (true) {
      case !!training:
        return 'deleteTraining';
      case isAssignedToAnotherRole || isWithinRoleTable:
        return 'roleRequired';
      case !!userCourse:
        return 'deleteUserCourse';
      case !!booking:
        return 'courseIsBooked';
      default:
        return null;
    }
  })();

  const prioritisedBookingStatus = (() => {
    switch (true) {
      case !booking && !trackedPersonnelContext.isTrackedPersonnelUsageLoaded:
        return 'loadingTrackedPersonnelUsage';
      case !booking &&
        !trackedPersonnelContext.isCurrentPersonnelTracked &&
        trackedPersonnelContext.hasReachedLimit:
        return 'trackedPersonnelLimitReached';
      case courseHasELearningCourse && !!registration:
        return 'eLearningInProgress';
      case !!booking && courseHasELearningCourse:
        return 'removeBooking';
      case courseHasELearningCourse && creditInfoUnavailable:
        return 'creditsInfoUnavailable';
      case courseHasELearningCourse && !currentPersonnel.email:
        return 'unbookableELearning';
      case courseHasELearningCourse:
        return 'bookElearning';
      case !booking:
        return 'addBooking';
      default:
        return null;
    }
  })();

  const deletionOption = {
    deleteTraining: (
      <OptionChoice
        onClick={(setOptionsOpen) =>
          optionsHandler(setOptionsOpen, () =>
            onDeleteTrainingClick({ training, course }),
          )
        }
        warning={true}
      >
        <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-red-600 hover:tw-text-red-600'>
          {i18nTCommon('training.deleteExistingTraining')}
        </a>
      </OptionChoice>
    ),
    roleRequired: (
      <OptionChoice disabled={true}>
        <Tooltip
          className='tooltip-dark--max-w-xxs'
          placement='left'
          tooltip={
            isAdditional ?
              i18nT('roleRequired.disabledTooltip.additional')
            : i18nT('required.disabledTooltip.required')
          }
          trigger='hover'
        >
          <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
            {i18nTCommon('courses.deleteCourse')}
          </a>
        </Tooltip>
      </OptionChoice>
    ),
    courseIsBooked: (
      <OptionChoice disabled={true}>
        <Tooltip
          className='tooltip-dark--max-w-xxs'
          placement='left'
          tooltip={i18nT('courseIsBooked.disabledTooltip')}
          trigger='hover'
        >
          <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
            {i18nTCommon('courses.deleteCourse')}
          </a>
        </Tooltip>
      </OptionChoice>
    ),
    deleteUserCourse: (
      <OptionChoice
        onClick={(setOptionsOpen) =>
          optionsHandler(setOptionsOpen, () =>
            onDeleteCourseClick({ userCourse, course }),
          )
        }
        warning={true}
      >
        <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-red-600 hover:tw-text-red-600'>
          {i18nTCommon('courses.deleteCourse')}
        </a>
      </OptionChoice>
    ),
  }[prioritisedDeletionStatus];

  const bookingOption = {
    loadingTrackedPersonnelUsage: (
      <OptionChoice disabled={true}>
        <Tooltip
          className='tooltip-dark--max-w-xxs'
          placement='left'
          tooltip={i18nT('loadingTrackedPersonnelUsage.disabledTooltip')}
          trigger='hover'
        >
          <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
            {i18nTCommon('bookings.addBooking')}
          </a>
        </Tooltip>
      </OptionChoice>
    ),
    trackedPersonnelLimitReached: (
      <OptionChoice disabled={true}>
        <Tooltip
          className='tooltip-dark--max-w-xxs'
          placement='left'
          tooltip={i18nT('trackedPersonnelLimitReached.disabledTooltip')}
          trigger='hover'
        >
          <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
            {i18nTCommon('bookings.addBooking')}
          </a>
        </Tooltip>
      </OptionChoice>
    ),
    eLearningInProgress: (
      <OptionChoice disabled={true}>
        <Tooltip
          className='tooltip-dark--max-w-xxs'
          placement='left'
          tooltip={i18nT('eLearningInProgress.disabledTooltip')}
          trigger='hover'
        >
          <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
            {i18nTCommon('bookings.cancelEnrolment')}
          </a>
        </Tooltip>
      </OptionChoice>
    ),
    removeBooking: (
      <OptionChoice
        onClick={(setOptionsOpen) =>
          optionsHandler(setOptionsOpen, () => onRemoveBooking(booking.id))
        }
      >
        <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
          {i18nTCommon('bookings.cancelEnrolment')}
        </a>
      </OptionChoice>
    ),
    creditsInfoUnavailable: (
      <OptionChoice disabled={true}>
        <Tooltip
          className='tooltip-dark--max-w-xxs'
          placement='left'
          tooltip={i18nTCommon('bookings.errors.creditInfoUnavailable')}
          trigger='hover'
        >
          <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
            {i18nTCommon('bookings.enrolELearning')}
          </a>
        </Tooltip>
      </OptionChoice>
    ),
    unbookableELearning: (
      <OptionChoice disabled={true}>
        <Tooltip
          className='tooltip-dark--max-w-xxs'
          placement='left'
          tooltip={i18nTCommon('personnel.errors.mustHaveEmailForELearning')}
          trigger='hover'
        >
          <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
            {i18nTCommon('bookings.enrolELearning')}
          </a>
        </Tooltip>
      </OptionChoice>
    ),
    bookElearning: (
      <OptionChoice
        onClick={(setOptionsOpen) =>
          optionsHandler(setOptionsOpen, () =>
            onAddELearningBooking({ course, eLearningCourse }),
          )
        }
      >
        <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
          {i18nTCommon('bookings.enrolELearning')}
        </a>
      </OptionChoice>
    ),
    addBooking: (
      <OptionChoice
        onClick={(setOptionsOpen) =>
          optionsHandler(setOptionsOpen, () => onAddBooking({ course }))
        }
      >
        <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
          {i18nTCommon('bookings.addBooking')}
        </a>
      </OptionChoice>
    ),
  }[prioritisedBookingStatus];

  const historicalTrainingChoice = (
    <OptionChoice
      onClick={(setOptionsOpen) => {
        setOptionsOpen(false);

        if (!isHistoricalTrainingVisible) {
          sendAnalytics('Show training history clicked', {
            currentUser: currentActor.user,
            personnelId,
          });
        }

        toggleHistoricalTraining();
      }}
    >
      <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
        {isHistoricalTrainingVisible ?
          i18nT('trainingHistory.hide')
        : i18nT('trainingHistory.show')}
      </a>
    </OptionChoice>
  );

  const newTrainingStatus = (() => {
    switch (true) {
      case !!registration:
        return 'elearningStarted';
      case !trackedPersonnelContext.isTrackedPersonnelUsageLoaded:
        return 'loadingTrackedPersonnelUsage';
      case !trackedPersonnelContext.isCurrentPersonnelTracked &&
        trackedPersonnelContext.hasReachedLimit:
        return 'trackedPersonnelLimitReached';
      default:
        return 'default';
    }
  })();

  const newTrainingOptionChoice = {
    loadingTrackedPersonnelUsage: (
      <OptionChoice disabled={true}>
        <Tooltip
          className='tooltip-dark--max-w-xxs'
          placement='left'
          tooltip={i18nT('loadingTrackedPersonnelUsage.disabledTooltip')}
          trigger='hover'
        >
          <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
            {i18nTCommon('training.recordNewTraining')}
          </a>
        </Tooltip>
      </OptionChoice>
    ),
    trackedPersonnelLimitReached: (
      <OptionChoice disabled={true}>
        <Tooltip
          className='tooltip-dark--max-w-xxs'
          placement='left'
          tooltip={i18nT('trackedPersonnelLimitReached.disabledTooltip')}
          trigger='hover'
        >
          <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
            {i18nTCommon('training.recordNewTraining')}
          </a>
        </Tooltip>
      </OptionChoice>
    ),
    elearningStarted: (
      <OptionChoice disabled={true}>
        <Tooltip
          className='tooltip-dark--max-w-xxs'
          placement='left'
          tooltip={i18nT('eLearningInProgress.disabledTooltip')}
          trigger='hover'
        >
          <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
            {i18nTCommon('training.recordNewTraining')}
          </a>
        </Tooltip>
      </OptionChoice>
    ),
    default: (
      <OptionChoice
        onClick={(setOptionsOpen) =>
          optionsHandler(setOptionsOpen, () =>
            onNewTrainingClick({ booking, course }),
          )
        }
      >
        <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
          {i18nTCommon('training.recordNewTraining')}
        </a>
      </OptionChoice>
    ),
  }[newTrainingStatus];

  return showHistoricalTrainingOnly ?
      <Options>{historicalTrainingChoice}</Options>
    : <Options onClose={onTrainingOptionsClose}>
        {newTrainingOptionChoice}
        <OptionChoice
          disabled={!training}
          onClick={(setOptionsOpen) =>
            optionsHandler(setOptionsOpen, () =>
              onEditTrainingClick({ training, course }),
            )
          }
        >
          <ConditionalWrapper
            condition={!training}
            wrapper={(children) => (
              <Tooltip
                className='tooltip-dark--max-w-xxs'
                placement='left'
                tooltip={i18nT('noTraining.disabledTooltip')}
                trigger='hover'
              >
                {children}
              </Tooltip>
            )}
          >
            <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
              {i18nTCommon('training.editExistingTraining')}
            </a>
          </ConditionalWrapper>
        </OptionChoice>
        {bookingOption}
        {!!booking && courseHasELearningCourse && (
          <OptionChoice
            disabled={!currentPersonnel.email}
            onClick={(setOptionsOpen) =>
              optionsHandler(setOptionsOpen, () =>
                onBookingReminderClick(booking.id),
              )
            }
          >
            <ConditionalWrapper
              condition={!currentPersonnel.email}
              wrapper={(children) => (
                <Tooltip
                  className='tooltip-dark--max-w-xxs'
                  placement='left'
                  tooltip={i18nTCommon('personnel.noEmail')}
                  trigger='hover'
                >
                  {children}
                </Tooltip>
              )}
            >
              <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
                {i18nTCommon('shared.sendReminder')}
              </a>
            </ConditionalWrapper>
          </OptionChoice>
        )}
        {currentActor.isAllowedFeature([
          'e_learning_auto_enrol',
          'e_learning',
          'training_register',
        ]) &&
          courseHasELearningCourse &&
          (function autoEnrolmentOptions() {
            const hasNoAutoEnrolmentBlockers =
              prioritisedAutoEnrolmentBlockers.length === 0;

            const isAutoEnrolling =
              hasNoAutoEnrolmentBlockers &&
              !currentAutoEnrolmentExclusionUserSelection;

            return (
              <OptionChoice
                disabled={!hasNoAutoEnrolmentBlockers}
                hoverClassName='hover:tw-bg-blue-025'
                onClick={() => {
                  onCurrentAutoEnrolmentExclusionUserSelectionChange(
                    !currentAutoEnrolmentExclusionUserSelection,
                  );
                }}
              >
                <ConditionalWrapper
                  condition={!hasNoAutoEnrolmentBlockers}
                  wrapper={(children) => (
                    <Tooltip
                      className='tooltip-dark--max-w-xxs'
                      placement='left'
                      tooltip={i18nT(
                        `autoEnrols.disabledTooltip.${prioritisedAutoEnrolmentBlockers[0]}`,
                      )}
                      trigger='hover'
                    >
                      {children}
                    </Tooltip>
                  )}
                >
                  <a className='tw-group/option tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2 tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
                    <span>{i18nT('autoEnrols.label')}</span>
                    <Switch
                      isChecked={isAutoEnrolling}
                      isDisabled={!hasNoAutoEnrolmentBlockers}
                    />
                  </a>
                </ConditionalWrapper>
              </OptionChoice>
            );
          })()}
        <OptionChoice
          hoverClassName='hover:tw-bg-blue-025'
          onClick={() => {
            onRequirementChange(!isCurrentlyRequired);
          }}
        >
          <a className='tw-group/option tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2 tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
            <span>{i18nT('required.label')}</span>
            <Switch isChecked={isCurrentlyRequired} />
          </a>
        </OptionChoice>
        {isMultipleTraining && historicalTrainingChoice}
        {deletionOption}
      </Options>;
}

TrainingOptions.propTypes = {
  showHistoricalTrainingOnly: PropTypes.bool,
  isWithinRoleTable: PropTypes.bool,
  isAdditional: PropTypes.bool,
  isMultipleTraining: PropTypes.bool,
  isHistoricalTrainingVisible: PropTypes.bool,
  courseHasELearningCourse: PropTypes.bool,
  prioritisedAutoEnrolmentBlockers: PropTypes.array,
  eLearningCourse: resourceShape('eLearningCourse'),
  booking: PropTypes.object,
  registration: PropTypes.object,
  training: PropTypes.object,
  creditInfoUnavailable: PropTypes.bool,
  toggleHistoricalTraining: PropTypes.func,
  onNewTrainingClick: PropTypes.func.isRequired,
  onEditTrainingClick: PropTypes.func.isRequired,
  onDeleteTrainingClick: PropTypes.func.isRequired,
  onDeleteCourseClick: PropTypes.func.isRequired,
  onAddBooking: PropTypes.func.isRequired,
  onAddELearningBooking: PropTypes.func.isRequired,
  onRemoveBooking: PropTypes.func.isRequired,
  onBookingReminderClick: PropTypes.func.isRequired,
  onTrainingOptionsClose: PropTypes.func.isRequired,
  isAssignedToAnotherRole: PropTypes.bool,
  personnelId: PropTypes.string,
  currentPersonnel: PropTypes.shape({
    email: PropTypes.string,
  }).isRequired,
  course: resourceShape('course'),
  userCourse: resourceShape('userCourse'),
  isCurrentlyRequired: PropTypes.bool.isRequired,
  currentAutoEnrolmentExclusionUserSelection: PropTypes.bool.isRequired,
  onRequirementChange: PropTypes.func.isRequired,
  onCurrentAutoEnrolmentExclusionUserSelectionChange: PropTypes.func.isRequired,
};

TrainingOptions.defaultProps = {
  isAdditional: false,
};
